<template>
  <div ref="root" class="financing-inquiry flex flex-col gap-6 text-blue-whale">
    <ol v-if="step !== 'thanks'" class="grid grid-cols-2 gap-4 m-0 p-0 list-none">
      <li v-for="(name, index) in STEPS" :key="name" class="flex flex-col gap-2">
        <span class="h-1 rounded-full" :class="index <= stepIndex ? 'bg-accent' : 'bg-neutral-lighter'" />
        <span class="text-[15px]" :class="{ 'font-semibold': name === step }">{{ t(`components.financingInquiry.steps.${name}`) }}</span>
      </li>
    </ol>

    <!-- Contact details -->
    <form v-if="step === 'contact'" class="flex flex-col gap-4" novalidate @submit.prevent="toSummary">
      <span class="font-semibold text-[17px]">{{ t('components.financingInquiry.contact.title') }}</span>
      <div class="grid gap-4 lg:grid-cols-2">
        <SharedInput
          v-if="isBusiness"
          :id="`${id}-company`"
          v-model="contact.company"
          name="company"
          type="text"
          autocomplete="organization"
          required
          :label="t('components.financingInquiry.contact.company')"
          :error="errors.company"
          class="lg:col-span-2"
        />
        <SharedInput
          :id="`${id}-firstName`"
          v-model="contact.firstName"
          name="firstName"
          type="text"
          autocomplete="given-name"
          required
          :label="t('components.financingInquiry.contact.firstName')"
          :error="errors.firstName"
        />
        <SharedInput
          :id="`${id}-lastName`"
          v-model="contact.lastName"
          name="lastName"
          type="text"
          autocomplete="family-name"
          required
          :label="t('components.financingInquiry.contact.lastName')"
          :error="errors.lastName"
        />
        <SharedInput
          :id="`${id}-email`"
          v-model="contact.email"
          name="email"
          type="email"
          autocomplete="email"
          required
          :label="t('components.financingInquiry.contact.email')"
          :error="errors.email"
        />
        <SharedInput
          :id="`${id}-phone`"
          v-model="contact.phone"
          name="phone"
          type="tel"
          autocomplete="tel"
          required
          :label="t('components.financingInquiry.contact.phone')"
          :error="errors.phone"
        />
        <div class="flex flex-col gap-2">
          <label :for="`${id}-location`" class="font-semibold">
            {{ t('components.financingInquiry.contact.location') }}<span aria-hidden="true">*</span>
          </label>
          <select :id="`${id}-location`" v-model="contact.location" name="location" class="w-full h-12 px-4 border border-java-light rounded-sm text-base bg-white">
            <option v-for="location in LOCATIONS" :key="location" :value="location">{{ locationLabel(location) }}</option>
          </select>
        </div>
        <SharedInput
          :id="`${id}-reachability`"
          v-model="contact.reachability"
          name="reachability"
          type="text"
          :label="t('components.financingInquiry.contact.reachability')"
        />
      </div>
      <SharedTextarea :id="`${id}-message`" v-model="contact.message" name="message" :rows="3" :label="t('components.financingInquiry.contact.message')" />
      <SharedCheckbox :id="`${id}-tradeIn`" v-model="contact.tradeIn" name="tradeIn">
        <span class="text-small-normal">{{ t('components.financingInquiry.contact.tradeIn') }}</span>
      </SharedCheckbox>
      <span class="text-[13px] text-blue-whale-light">{{ t('components.financingInquiry.requiredFields') }}</span>
      <div class="flex flex-col-reverse sm:flex-row justify-between gap-3">
        <SharedButton :label="t('components.financingInquiry.backToCalculator')" variant="secondary" @click="emit('back')" />
        <SharedButton :label="t('components.financingInquiry.next')" @click="toSummary" />
      </div>
    </form>

    <!-- Summary and declarations -->
    <div v-else-if="step === 'summary'" class="flex flex-col gap-5">
      <span class="font-semibold text-[17px]">{{ t('components.financingInquiry.summary.title') }}</span>

      <div class="flex flex-col gap-2.5 p-4 border border-java-light rounded-sm">
        <div class="flex justify-between items-baseline gap-4 flex-wrap">
          <div class="flex flex-col">
            <span class="font-semibold">{{ vehicleName }}</span>
            <span class="text-[13px] text-blue-whale-light">{{ t(`components.financingCalculator.resultTitle.${input.mode}`) }}</span>
          </div>
          <button type="button" class="text-[13px] underline" @click="emit('back')">{{ t('components.financingInquiry.summary.changeCalculation') }}</button>
        </div>
        <FinancingWarning />
        <dl class="m-0 flex flex-col">
          <div v-for="row in rows" :key="row.label" class="grid grid-cols-[minmax(0,1fr)_auto] gap-x-4 py-[3px] border-b border-neutral-lighter">
            <dt class="text-[15px] leading-snug">{{ row.label }}</dt>
            <dd class="m-0 text-[15px] leading-snug font-semibold text-right whitespace-nowrap">{{ row.value }}</dd>
            <span v-if="row.note" class="col-span-2 text-[13px] leading-snug">{{ row.note }}</span>
          </div>
        </dl>
        <span class="text-[13px] text-blue-whale-light">
          {{ t('components.financingInquiry.summary.exampleNote') }}
          {{ t(showNet ? 'components.financingCalculator.vatNote.net' : 'components.financingCalculator.vatNote.gross') }}
        </span>
      </div>

      <div class="flex flex-col gap-2 p-4 border border-java-light rounded-sm">
        <div class="flex justify-between items-baseline gap-4">
          <span class="font-semibold">{{ t('components.financingInquiry.summary.contact') }}</span>
          <button type="button" class="text-[13px] underline" @click="step = 'contact'">{{ t('components.financingInquiry.summary.change') }}</button>
        </div>
        <dl class="m-0 grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-1 text-[15px]">
          <template v-for="row in contactRows" :key="row.label">
            <dt class="text-blue-whale-light">{{ row.label }}</dt>
            <dd class="m-0 break-words">{{ row.value }}</dd>
          </template>
        </dl>
      </div>

      <div class="flex flex-col gap-3">
        <span class="font-semibold">{{ t('components.financingInquiry.info.title') }}</span>
        <ul class="m-0 p-0 list-none flex flex-col gap-2">
          <li v-for="point in INFO_POINTS" :key="point" class="flex gap-2.5 text-[15px] leading-snug">
            <svg class="w-5 h-5 flex-none mt-px text-accent" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
              <path d="m5 12.5 4.5 4.5L19 7.5" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
            <span>{{ t(`components.financingInquiry.info.${point}`) }}</span>
          </li>
        </ul>
      </div>

      <div class="flex flex-col gap-3">
        <span class="font-semibold">{{ t('components.financingInquiry.declarations.title') }}</span>
        <SharedCheckbox
          :id="`${id}-nonBindingExample`"
          v-model="declarations.nonBindingExample"
          name="nonBindingExample"
          required
          :error="declarationErrors.nonBindingExample"
        >
          <span class="text-small-normal">{{ t('components.financingInquiry.declarations.nonBindingExample') }}<span aria-hidden="true"> *</span></span>
        </SharedCheckbox>
        <SharedCheckbox :id="`${id}-privacyNotice`" v-model="declarations.privacyNotice" name="privacyNotice" required :error="declarationErrors.privacyNotice">
          <span class="text-small-normal">{{ t('components.financingInquiry.declarations.privacyNotice') }}<span aria-hidden="true"> *</span></span>
        </SharedCheckbox>
        <SharedCheckbox :id="`${id}-newsletter`" v-model="newsletter" name="newsletter">
          <span class="text-small-normal">{{ t('components.financingInquiry.declarations.newsletter') }}</span>
        </SharedCheckbox>
        <p class="m-0 text-[13px] leading-normal text-blue-whale-light">
          <i18n-t keypath="components.financingInquiry.declarations.privacyText" scope="global">
            <NuxtLink class="underline" target="_blank" rel="noopener noreferrer" :to="dataProtectionPolicyUrl">
              {{ t('components.financingInquiry.declarations.privacyLink') }}
            </NuxtLink>
          </i18n-t>
        </p>
      </div>

      <FinancingWarning />

      <div v-if="needsTurnstile" class="flex flex-col gap-2">
        <p class="m-0 text-[15px]">{{ t('components.financingInquiry.turnstile') }}</p>
        <ClientOnly>
          <NuxtTurnstile :key="turnstileKey" v-model="turnstileToken" />
        </ClientOnly>
      </div>
      <p v-if="sendFailed" role="alert" class="m-0 px-3.5 py-2.5 rounded-sm border border-christine text-[15px]">{{ t('components.financingInquiry.sendFailed') }}</p>

      <div class="flex flex-col-reverse sm:flex-row justify-between gap-3">
        <SharedButton :label="t('components.financingInquiry.back')" variant="secondary" @click="step = 'contact'" />
        <SharedButton :label="t('components.financingInquiry.send')" :loading="sending" :disabled="sending" @click="send" />
      </div>
    </div>

    <!-- Thanks -->
    <div v-else class="flex flex-col gap-5">
      <p class="m-0 text-medium-normal">{{ t('components.financingInquiry.thanks.text', { name: contact.firstName.trim(), vehicle: vehicleName }) }}</p>
      <ol class="m-0 p-0 list-none flex flex-col gap-4">
        <li v-for="(name, index) in NEXT_STEPS" :key="name" class="flex gap-3">
          <span class="flex-none size-8 rounded-full bg-accent text-white font-semibold flex items-center justify-center">{{ index + 1 }}</span>
          <div class="flex flex-col gap-0.5">
            <span class="font-semibold">{{ t(`components.financingInquiry.thanks.steps.${name}.title`) }}</span>
            <span class="text-[15px]">{{ t(`components.financingInquiry.thanks.steps.${name}.text`, { location: locationLabel(contact.location) }) }}</span>
          </div>
        </li>
      </ol>
      <span class="text-[15px]">{{ t('components.financingInquiry.thanks.reference', { reference }) }}</span>
      <div>
        <SharedButton :label="t('components.financingInquiry.thanks.close')" @click="emit('close')" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { isValidPhoneNumber } from 'libphonenumber-js';
  import type { FetchError } from 'ofetch';
  import { z } from 'zod';
  import type { FinancingInput, FinancingInquiryContact, FinancingInquiryLocation, FinancingInquiryRequest, FinancingInquiryResponse, Quote } from '#shared/financing';
  import { resolveDealerLocation } from '#shared/dealer-locations';
  import type { StrapiResponse, SiteOption } from '~/types/strapi';
  import type { FinancingCalculatorVehicle } from '../FinancingCalculator/index.vue';
  import FinancingWarning from '../FinancingCalculator/Warning.vue';

  /** The calculator's vehicle, with its location to preselect the dealership. */
  export interface FinancingInquiryVehicle extends FinancingCalculatorVehicle {
    location?: string | null;
  }

  const props = defineProps<{
    vehicle: FinancingInquiryVehicle;
    /** The terms and the example the calculator showed. */
    input: FinancingInput;
    quote: Quote;
  }>();
  const emit = defineEmits<{
    /** Back to the calculator, to change the terms. */
    (e: 'back' | 'close'): void;
    (e: 'sent', payload: { reference: string; contact: FinancingInquiryContact }): void;
  }>();
  const { t, locale } = useI18n();
  const id = useId();

  const STEPS = ['contact', 'summary'] as const;
  const LOCATIONS: FinancingInquiryLocation[] = ['gleisdorf', 'gleisdorf-sued', 'oberwart'];
  const NEXT_STEPS = ['contact', 'offer', 'decision'];
  const INFO_POINTS = ['nonBinding', 'lender', 'data', 'standardInformation'];
  const step = ref<(typeof STEPS)[number] | 'thanks'>('contact');
  const stepIndex = computed(() => (STEPS as readonly string[]).indexOf(step.value));
  // Each step starts at its top, also in the scrolling modal.
  const root = ref<HTMLElement | null>(null);
  watch(step, () => root.value?.scrollIntoView?.({ behavior: 'smooth', block: 'start' }));

  const isBusiness = computed(() => props.input.customer === 'firma');
  const vehicleName = computed(() => [props.vehicle.make?.name, props.vehicle.model].filter(Boolean).join(' '));
  const siteOptions = inject<Ref<StrapiResponse<SiteOption>> | undefined>('siteOptions', undefined);
  const dataProtectionPolicyUrl = computed(() => useLocalizedPath(siteOptions?.value?.data?.dataProtectionPolicy?.slug || ''));

  const { rows, showNet } = useFinancingExample(
    () => props.vehicle,
    () => props.input,
    () => props.quote
  );
  const locationLabel = (location: FinancingInquiryLocation) => t(`components.financingInquiry.locations.${location}`);

  const contact = reactive({
    company: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    location: (resolveDealerLocation(props.vehicle.location)?.id ?? 'gleisdorf') as FinancingInquiryLocation,
    reachability: '',
    message: '',
    tradeIn: false,
  });

  const required = () => z.string().trim().min(1, t('validation.required'));
  const contactSchema = computed(() =>
    z.object({
      company: isBusiness.value ? required() : z.string(),
      firstName: required(),
      lastName: required(),
      email: required().email(t('validation.email')),
      // The dealership is in Austria: numbers without a country code are Austrian.
      phone: required().refine(value => isValidPhoneNumber(value, 'AT'), t('validation.phoneNumber')),
    })
  );

  // Shown once the visitor tried to go on, then kept up to date while they correct.
  const showErrors = ref(false);
  const errors = computed<Partial<Record<keyof typeof contact, string>>>(() => {
    if (!showErrors.value) return {};
    const result = contactSchema.value.safeParse(contact);
    if (result.success) return {};
    return Object.fromEntries(result.error.issues.map(issue => [issue.path[0], issue.message]));
  });

  function toSummary() {
    showErrors.value = true;
    if (Object.keys(errors.value).length) return;
    step.value = 'summary';
  }

  const contactRows = computed(() => [
    ...(isBusiness.value ? [{ label: t('components.financingInquiry.contact.company'), value: contact.company }] : []),
    {
      label: t(isBusiness.value ? 'components.financingInquiry.summary.contactPerson' : 'components.financingInquiry.summary.name'),
      value: `${contact.firstName} ${contact.lastName}`.trim(),
    },
    { label: t('components.financingInquiry.summary.email'), value: contact.email },
    { label: t('components.financingInquiry.summary.phone'), value: contact.phone },
    { label: t('components.financingInquiry.summary.location'), value: locationLabel(contact.location) },
    ...(contact.reachability.trim() ? [{ label: t('components.financingInquiry.summary.reachability'), value: contact.reachability }] : []),
    ...(contact.message.trim() ? [{ label: t('components.financingInquiry.summary.message'), value: contact.message }] : []),
    ...(contact.tradeIn ? [{ label: t('components.financingInquiry.summary.tradeIn'), value: t('components.financingInquiry.summary.tradeInWanted') }] : []),
  ]);

  const declarations = reactive({ nonBindingExample: false, privacyNotice: false });
  const newsletter = ref(false);
  const triedSending = ref(false);
  const declarationErrors = computed(() => ({
    nonBindingExample: triedSending.value && !declarations.nonBindingExample ? t('components.financingInquiry.declarations.nonBindingExampleMissing') : undefined,
    privacyNotice: triedSending.value && !declarations.privacyNotice ? t('components.financingInquiry.declarations.privacyNoticeMissing') : undefined,
  }));

  const sending = ref(false);
  const sendFailed = ref(false);
  const reference = ref('');

  // The check from the calculator lasts 15 minutes; a visitor who took longer passes a new one,
  // then the inquiry goes out without another click.
  const { verifyTurnstileToken } = useValidTurnstileTokenCookie();
  const needsTurnstile = ref(false);
  const turnstileToken = ref('');
  // Bound to the widget's :key; a new key re-renders it with a fresh challenge.
  const turnstileKey = ref(0);
  watch(turnstileToken, async token => {
    if (!token) return;
    if (!(await verifyTurnstileToken(token))) {
      turnstileKey.value++;
      return;
    }
    needsTurnstile.value = false;
    await send();
  });

  /** The contact details as the server takes them: trimmed, without empty optional fields. */
  function contactToSend(): FinancingInquiryContact {
    const optional = (value: string) => value.trim() || undefined;
    return {
      company: isBusiness.value ? contact.company.trim() : undefined,
      firstName: contact.firstName.trim(),
      lastName: contact.lastName.trim(),
      email: contact.email.trim(),
      phone: contact.phone.trim(),
      location: contact.location,
      reachability: optional(contact.reachability),
      message: optional(contact.message),
      tradeIn: contact.tradeIn,
    };
  }

  async function send() {
    triedSending.value = true;
    if (!declarations.nonBindingExample || !declarations.privacyNotice || sending.value) return;

    const body: FinancingInquiryRequest = {
      vehicleId: props.vehicle.documentId,
      language: locale.value as 'de' | 'en',
      input: props.input,
      quote: props.quote,
      contact: contactToSend(),
      confirmations: { nonBindingExample: true, privacyNotice: true },
      newsletter: newsletter.value,
    };

    sending.value = true;
    sendFailed.value = false;
    try {
      const response = await $fetch<FinancingInquiryResponse>('/api/financing-inquiry', { method: 'POST', body });
      reference.value = response.reference;
      step.value = 'thanks';
      emit('sent', { reference: response.reference, contact: body.contact });
    } catch (error) {
      if ((error as FetchError).statusCode === 403) {
        needsTurnstile.value = true;
      } else {
        sendFailed.value = true;
      }
    } finally {
      sending.value = false;
    }
  }
</script>
